import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Calendar, User, Bell, LogOut, Settings, Users as UsersIcon, Briefcase, Building2, FileText, Package, ShoppingCart, Calculator } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import NotificationPanel from '../components/NotificationPanel';
import AlarmNotifications from '../components/AlarmNotifications';

export const Dashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const [unreadCounts, setUnreadCounts] = useState({});
  
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
    } catch (error) {
      console.error('Okunmamış sayıları alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  useEffect(() => {
    fetchUnreadCounts();
  }, [location.pathname, fetchUnreadCounts]);

  // Refresh user data periodically to get updated panel_access
  useEffect(() => {
    // Refresh immediately on mount and every 60 seconds
    refreshUser();
    const userRefreshInterval = setInterval(() => {
      refreshUser();
    }, 60000); // 60 seconds
    
    return () => clearInterval(userRefreshInterval);
  }, [refreshUser]);
  
  // Check if we're in dashboard or in a panel
  const isInDashboard = location.pathname.startsWith('/dashboard');

  // Calculate total İK Talepleri badge (hr_requests + document_requests)
  const ikTalepleriTotal = (unreadCounts.my_hr_requests || 0) + (unreadCounts.hr_document_requests || 0);

  const menuItems = [
    { path: 'messages', icon: MessageSquare, label: 'Mesajlar', badgeKey: 'messages' },
    { path: 'announcements', icon: Bell, label: 'Şirket Bildirimleri', badgeKey: 'announcements' },
    { path: 'hr-requests', icon: FileText, label: 'İK Talepleri', badgeKey: 'ik_talepleri_total' },
    { path: 'my-info', icon: User, label: user?.name || 'Bilgilerim', badgeKey: null, highlight: true },
  ];

  // Custom badge counts (for calculated values)
  const getBadgeCount = (badgeKey) => {
    if (badgeKey === 'ik_talepleri_total') return ikTalepleriTotal;
    return unreadCounts[badgeKey] || 0;
  };

  const isActive = (path) => location.pathname.includes(path);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="dashboard-container">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/70 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_independent-erp/artifacts/prgex85y_18_01_2025_fortis_kurumsal_logolar.svg" 
              alt="FORTIS Logo" 
              className="h-[85px] w-auto"
            />
          </div>
          
          <div className="flex-1 flex justify-center">
            <Link to="messages">
              <h1 className="text-2xl font-bold uppercase tracking-wide cursor-pointer hover:text-[hsl(var(--brand-700))] transition-colors">
                FORTIS ERP
              </h1>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <Link to="/dashboard/messages">
              <span className="text-sm font-medium cursor-pointer hover:text-[hsl(var(--brand-700))] transition-colors">
                {user?.name}
              </span>
            </Link>
            
            {/* Show panel button when in Dashboard, show home button when in panel */}
            {isInDashboard ? (
              <>
                {/* Admin Panel Button - check panel_access array */}
                {(user?.panel_access?.includes('admin') || user?.role === 'admin') && (
                  <Link to="/admin/users">
                    <Button variant="outline" size="sm" className="gap-2" data-testid="admin-panel-btn">
                      <Settings className="h-4 w-4" />
                      Admin Paneli
                    </Button>
                  </Link>
                )}
                
                {/* HR Panel Button - check panel_access array */}
                {(user?.panel_access?.includes('hr') || user?.role === 'hr_manager') && (
                  <Link to="/hr/employees">
                    <Button variant="outline" size="sm" className="gap-2 relative" data-testid="hr-panel-btn">
                      <UsersIcon className="h-4 w-4" />
                      İK Paneli
                      {(unreadCounts.hr_inbox > 0 || unreadCounts.hr_management_rejected > 0) && (
                        <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 min-w-5 p-0 flex items-center justify-center text-xs">
                          {(unreadCounts.hr_inbox || 0) + (unreadCounts.hr_management_rejected || 0)}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                )}

                {/* Work Panel Button - check panel_access array */}
                {(user?.panel_access?.includes('work')) && (
                  <Link to="/work/dashboard">
                    <Button variant="outline" size="sm" className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="work-panel-btn">
                      <Briefcase className="h-4 w-4" />
                      İş Paneli
                    </Button>
                  </Link>
                )}

                {/* Branch Management Panel Button - check panel_access array */}
                {(user?.panel_access?.includes('branch')) && (
                  <Link to="/branch/dashboard">
                    <Button variant="outline" size="sm" className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 relative" data-testid="branch-panel-btn">
                      <Building2 className="h-4 w-4" />
                      Şube Yönetimi
                      {(unreadCounts.branch_inbox > 0 || unreadCounts.branch_hr_rejected > 0 || unreadCounts.branch_management_rejected > 0) && (
                        <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 min-w-5 p-0 flex items-center justify-center text-xs">
                          {(unreadCounts.branch_inbox || 0) + (unreadCounts.branch_hr_rejected || 0) + (unreadCounts.branch_management_rejected || 0)}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                )}

                {/* Board (Yönetim Kurulu) Panel Button */}
                {(user?.panel_access?.includes('board') || user?.role === 'yonetim_kurulu') && (
                  <Link to="/board/dashboard">
                    <Button variant="outline" size="sm" className="gap-2 border-[hsl(var(--brand-400))] text-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-50))] relative" data-testid="board-panel-btn">
                      <Building2 className="h-4 w-4" />
                      Yönetim Kurulu
                      {unreadCounts.board_pending > 0 && (
                        <Badge variant="destructive" className="absolute -top-2 -right-2 h-5 min-w-5 p-0 flex items-center justify-center text-xs">
                          {unreadCounts.board_pending}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                )}

                {/* Depo Paneli Button */}
                {(user?.panel_access?.includes('depo') || user?.panel_access?.includes('depot')) && (
                  <Link to="/depot/entry">
                    <Button variant="outline" size="sm" className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="depot-panel-btn">
                      <Package className="h-4 w-4" />
                      Depo Paneli
                    </Button>
                  </Link>
                )}

                {/* Satın Alma Paneli Button */}
                {(user?.panel_access?.includes('purchasing')) && (
                  <Link to="/purchasing/requests">
                    <Button variant="outline" size="sm" className="gap-2 border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="purchasing-panel-btn">
                      <ShoppingCart className="h-4 w-4" />
                      Satın Alma Paneli
                    </Button>
                  </Link>
                )}

                {/* Muhasebe Paneli Button */}
                {(user?.panel_access?.includes('accounting')) && (
                  <Link to="/accounting/income">
                    <Button variant="outline" size="sm" className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="accounting-panel-btn">
                      <Calculator className="h-4 w-4" />
                      Muhasebe Paneli
                    </Button>
                  </Link>
                )}
              </>
            ) : (
              /* Home/Dashboard button when in panels */
              <Link to="/dashboard/messages">
                <Button variant="outline" size="sm" className="gap-2" data-testid="back-to-dashboard-btn">
                  <User className="h-4 w-4" />
                  Ana Sayfa
                </Button>
              </Link>
            )}
            
            {/* Notification Panel */}
            <NotificationPanel />
            
            {/* Alarm Notifications */}
            <AlarmNotifications />
            
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'bg-[hsl(var(--brand-700))] text-white'
                      : 'hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]'
                  }`}
                  data-testid={`menu-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {item.badgeKey && getBadgeCount(item.badgeKey) > 0 && (
                    <Badge className={`ml-auto ${
                      isActive(item.path) ? 'bg-white text-[hsl(var(--brand-700))]' : 'bg-red-500 text-white'
                    }`}>
                      {getBadgeCount(item.badgeKey)}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
